import { useEffect, useState } from 'react';
import type { RatesSettings } from '@/settings/types';
import { fetchRates } from '@/api/rates';
import { CEB_REV_2026_DEFAULTS } from '@/settings/defaults';
import { CalculatorForm, type CalculatorInputs } from '@/components/CalculatorForm';
import { ResultsPanel } from '@/components/ResultsPanel';

export function CalculatorPage() {
  const [rates, setRates] = useState<RatesSettings | null>(null);
  const [ratesFallback, setRatesFallback] = useState(false);
  const [inputs, setInputs] = useState<CalculatorInputs | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchRates();
        if (!cancelled) setRates(r);
      } catch {
        if (!cancelled) {
          setRates(CEB_REV_2026_DEFAULTS);
          setRatesFallback(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!rates) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">Loading rates…</div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
      {ratesFallback && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Couldn't reach the server — using built-in defaults. Some rate edits may be missing.
        </div>
      )}

      <header>
        <h1 className="text-lg sm:text-2xl font-bold text-slate-900 leading-tight">
          Save on your fan bill
        </h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
          {rates.tariff.revisionLabel}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 sm:gap-6">
        <CalculatorForm rates={rates} onChange={setInputs} />
        <div>
          {inputs ? (
            <ResultsPanel inputs={inputs} rates={rates} />
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 p-6 sm:p-8 text-center text-slate-500 bg-white">
              Enter your fan count and usage to see the savings.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
